import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmptyReason } from '../EmptyReason/EmptyReason';
import { IExtendedBusinessDataProps } from '../BusinessDataSet/BusinessDataSet';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { findTableMinWidth } from '../../../Const/BusinessConfigs';
import EDDataGrid, {
  RowComponentType,
} from '../../../../../../BaseComponents/XGrid/EDDataGrid';
import {
  GridRow,
  useGridApiRef,
  GridValidRowModel,
  GridCellParams,
  GridEventListener,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import {
  getColDefinitions,
  rowsToDataGridRows,
  sortModelToSortProperties,
  sortPropertiesToSortModel,
  tableHasInfoColumn,
} from './BusinessDataTableUtils';
import { safeGetAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { move } from 'ramda';
import EmptyFilteredComponent from '../EmptyFilteredComponent/EmptyFilteredComponent';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import BusinessDataForm, {
  BusinessDataFormContext,
} from '../BusinessDataForm/BusinessDataForm';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { SUMROWID } from './Consts/BusinessDataTableConsts';
import { useBreakpointContext } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import useRegisterScrollTarget from '../../../../../../Components/AppNavbar/useRegisterScrollTarget';

// export interface ITableDef extends GridColDef {
//   header?: boolean;
// }
export type ITableDef = GridColDef;

const EmptyView: React.FC<
  React.PropsWithChildren<IExtendedBusinessDataProps>
> = props => {
  if ((props.urval || props.urvalId) && !props.loading) {
    return <EmptyFilteredComponent />;
  } else {
    return (
      <EmptyReason
        urval={props.urval}
        setUrval={props.setUrval}
        collection={props.collection}
        refetch={props.refetch}
        hasData={props.sortedData.rows.length > 0}
        loading={props.loading}
        notFullPage
      />
    );
  }
};
interface IRowExtraProps {
  associationPath?: string;
  collection: IClientBusinessModel;
}

/** Visar raderna med samma komponent som standard, men lägger en BusinessDataForm runt varje rad */
const RowWithBusinessDataForm: RowComponentType<IRowExtraProps> = props => {
  const { associationPath, collection, ...rest } = props;
  return (
    <BusinessDataForm
      isNew={false}
      association={props.associationPath}
      associatedId={props.rowId as string}
      model={props.collection}
      trace={`${props.associationPath}-${props.rowId}`}
    >
      <GridRow {...rest} data-testid={`GridRow-${associationPath}`} />
    </BusinessDataForm>
  );
};
interface ITableEditModeContext {
  editMode: boolean;
}
const defaultEditModeContext: ITableEditModeContext = {
  editMode: false,
};
export const TableEditModeContext = React.createContext<ITableEditModeContext>(
  defaultEditModeContext
);

/**
 * Komponent som visar en tabell med businessdata.
 */
export const BusinessDataTable: React.FunctionComponent<
  React.PropsWithChildren<IExtendedBusinessDataProps>
> = props => {
  const { selectDetail, onRowClick: onRowClickProp, isCellDisabled } = props;
  const { avenyCollectionMap } = useContext(ModelContext);
  const businessDataFormContext = useContext(BusinessDataFormContext);
  const apiRef = useGridApiRef();
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpointContext = useBreakpointContext();
  const formEditMode = businessDataFormContext?.formState.editMode;
  const [tableEditMode, setTableEditMode] = useState<boolean>(false);

  /** Vi skapara ett context med editMode och möjlighet att sätta mode för raderna */
  const tableEditModeContextValue = useMemo(
    () => ({ editMode: tableEditMode }),
    [tableEditMode]
  );

  const startTableEditMode = useCallback(() => setTableEditMode(true), []);
  const stopTableEditMode = useCallback(() => setTableEditMode(false), []);

  const tableEditable = Boolean(props.editable && formEditMode);

  const handleClick = useCallback(
    (id: TSelectedDetail) => {
      if (selectDetail && id) {
        selectDetail(id);
      }
    },
    [selectDetail]
  );

  const getDefaultMinWidth = (attributeDef: string): number | undefined => {
    if (attributeDef === 'InfoColumn') {
      return 36;
    } else if (!attributeDef) {
      return undefined;
    }
    const attributeInfo = safeGetAttributeInfo(
      props.collection,
      attributeDef,
      avenyCollectionMap
    );
    if (attributeInfo.isOk()) {
      return findTableMinWidth(
        attributeInfo.value.collection.CollectionName,
        attributeInfo.value.attribute.AttributeName
      );
    } else {
      return 10;
    }
  };

  const colIsRightAligned = (field: string): 'right' | undefined => {
    const attributeInfo = safeGetAttributeInfo(
      props.collection,
      field,
      avenyCollectionMap
    );

    const isRightAligned =
      attributeInfo.isOk() &&
      (attributeInfo.value.attribute.DataType === 'Currency' ||
        attributeInfo.value.attribute.DataType === 'Int' ||
        attributeInfo.value.attribute.DataType === 'Float');

    return isRightAligned ? 'right' : undefined;
  };

  const columns = useMemo(
    () =>
      getColDefinitions(
        props.columns,
        props.collection,
        avenyCollectionMap,
        props.colors,
        getDefaultMinWidth,
        colIsRightAligned,
        props.useFunktionsMenyItems,
        props.hideDefaultFunktionsMenyItems,
        props.businessIcons,
        props,
        tableEditable,
        props.association,
        props.isCellDisabled,
        breakpointContext
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.columns]
  );

  const rows: GridValidRowModel[] = useMemo(() => {
    const rows = rowsToDataGridRows(
      props.columns,
      props.sortedData.rows,
      props.collection
    );

    const sumRow = props.sumColumns?.reduce<{
      id: string;
      [key: string]: unknown;
    }>(
      (row, sumColumn) => {
        row[sumColumn.columnName] = rows.reduce((sum, element) => {
          return sum + (element[sumColumn.columnName] ?? 0);
        }, 0);
        return row;
      },
      { id: SUMROWID }
    );
    return sumRow ? [...rows, sumRow] : rows;
  }, [
    props.collection,
    props.columns,
    props.sortedData.rows,
    props.sumColumns,
  ]);

  const onCellClick = useCallback(
    (params: GridCellParams) => {
      if (tableEditable && params.colDef.editable && params.isEditable) {
        // Gå in i edit läge
        if (!tableEditMode) {
          startTableEditMode();
        }
      } else {
        // T.ex. om vi klickar på ett disableat fält så vill vi avsluta radredigeringen
        const gridEditRows = apiRef.current?.state.editRows;
        if (gridEditRows?.[params.row.id]) {
          stopTableEditMode();
        }
      }
    },
    [
      tableEditable,
      apiRef,
      tableEditMode,
      startTableEditMode,
      stopTableEditMode,
    ]
  );

  const EmptyViewWrapper: React.FC = () => <EmptyView {...props} />;

  const onRowClick: GridEventListener<'rowClick'> = useCallback(
    (params: GridRowParams, event, details) => {
      if (
        // params.colDef.field !== 'MenuColumn' &&
        !tableEditable &&
        params.row.id !== SUMROWID
      ) {
        const rowIndex = props.sortedData.rows.findIndex(
          row => row.id === params.row.id
        );
        const prevId = props.sortedData.rows[rowIndex - 1]?.id;
        const nextId = props.sortedData.rows[rowIndex + 1]?.id;
        handleClick({
          id: params.row.id as string,
          prevId,
          nextId,
          data: params.row.collectionData,
        });
      }
      onRowClickProp?.(params, event, details);
    },
    [tableEditable, onRowClickProp, props.sortedData.rows, handleClick]
  );
  const onCellKeyDown = useCallback<GridEventListener<'cellKeyDown'>>(
    (params, event) => {
      //enter i menykolumnen ska öppna menyn och inte gå ner till nästa rad
      if (
        params.field === 'MenuColumn' &&
        params.cellMode === 'edit' &&
        event.code === 'Enter'
      ) {
        event.defaultMuiPrevented = true;
      }
      //tab ska gå till nästa edit-komponent i editläge och inte till cellen mui vill
      if (params.cellMode === 'edit' && event.code === 'Tab') {
        event.defaultMuiPrevented = true;
      }
    },
    []
  );

  const rowProps: IRowExtraProps | undefined = useMemo(
    () =>
      tableEditable
        ? {
            associationPath: props.associationPath,
            collection: props.collection,
          }
        : undefined,
    [props.associationPath, props.collection, tableEditable]
  );
  const sortModel = useMemo(
    () => sortPropertiesToSortModel(props.sortedData.properties),
    [props.sortedData.properties]
  );

  const isCellEditable = useMemo(
    () =>
      isCellDisabled
        ? (gridCellParams: GridCellParams) => {
            return !isCellDisabled?.(gridCellParams);
          }
        : undefined,
    [isCellDisabled]
  );

  //om man går ur rad-editering med esc så vill vi också stänga av editeringsläget för tabellen, annars får det vara kvar och stängs när fokus ev. hamnar utanför tabellen
  const onRowEditStop = useCallback<GridEventListener<'rowEditStop'>>(
    params => {
      if (params.reason === 'escapeKeyDown') {
        stopTableEditMode();
      }
    },
    [stopTableEditMode]
  );

  const onBlur = useCallback<() => void>(() => {
    stopTableEditMode();
  }, [stopTableEditMode]);

  const onRowEditStart = useCallback<GridEventListener<'rowEditStart'>>(() => {
    startTableEditMode();
  }, [startTableEditMode]);

  useRegisterScrollTarget({
    containerRef,
    selector: '.MuiDataGrid-virtualScroller',
    enabled: !props.selectedId,
  });

  // kör window.debug = { showBreakpoint:true } i konsollen för att visa aktuell breakpoint för felsökning. Sätts aldrig i produktion annars.
  const showBreakpointText = !!window.debug?.showBreakpoint;

  return (
    <TableEditModeContext.Provider value={tableEditModeContextValue}>
      {showBreakpointText && (
        <EDBox position="absolute" top="auto" left="auto" zIndex={1000}>
          <EDTypography variant="caption" color="hotpink">
            {breakpointContext.localBreakPoint}
          </EDTypography>
        </EDBox>
      )}
      <EDDataGrid
        containerRef={containerRef}
        floatingMenuBottomSpacing={props.floatingMenuBottomSpacing}
        apiRef={apiRef}
        columns={columns}
        columnBorders={tableEditable}
        rows={rows}
        loading={props.loading}
        selectedId={props.selectedId}
        selectionGrid={props.selectionGrid}
        disabled={props.disabled}
        emptyView={EmptyViewWrapper}
        RowComponent={tableEditable ? RowWithBusinessDataForm : undefined}
        autoHeight={props.autoHeight ?? Boolean(props.inCard)}
        rowHeight={
          businessDataFormContext?.formState.editMode && tableEditable
            ? 39
            : undefined
        }
        onCellClick={
          props.selectDetail || tableEditable ? onCellClick : undefined
        }
        onCellKeyDown={onCellKeyDown}
        onBlur={tableEditable && formEditMode ? onBlur : undefined}
        onColumnOrderChange={(params, event, details) => {
          if (props.setColumns) {
            // Om vi har infocolumnen i tabellen så måste vi justera index
            const shiftIndexAmount = tableHasInfoColumn(
              props.colors,
              props.collectionStylesConfig
            )
              ? 1
              : 0;

            const movedColumns = move(
              params.oldIndex - shiftIndexAmount,
              params.targetIndex - shiftIndexAmount,
              props.columns
            );

            props.setColumns(movedColumns);
          }
        }}
        onRowClick={
          props.onRowClick || props.selectDetail ? onRowClick : undefined
        }
        sortModel={sortModel}
        onSortModelChange={model => {
          props.updateSortOrder({
            ...props.sortedData,
            properties: sortModelToSortProperties(model, props.columns),
          });
        }}
        emptyViewProps={props}
        rowProps={rowProps}
        testId="BusinessDataTable"
        settingsId={props.settingsId}
        isCellEditable={isCellEditable}
        editable={tableEditable}
        editMode={formEditMode}
        onRowEditStart={onRowEditStart}
        onRowEditStop={onRowEditStop}
        cellTabIndex={tableEditMode === true ? -1 : undefined}
      />
    </TableEditModeContext.Provider>
  );
};
